import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, ArrowLeft, Box, CheckCircle2, ClipboardCheck, Gem, PackageCheck, ShieldCheck, Sparkles, UploadCloud } from 'lucide-react';
import { productionApi } from '../api';
import SkeletonLoader from './components/SkeletonLoader';
import ApiButton from './components/ApiButton';
import { BREAKPOINTS } from './productionTheme';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMasterData } from '../../utils/useMasterData';

const initialQcForm = { weight_verified: '', stone_count: '', hallmark_ok: false, finish_grade: 'A', notes: '' };
const initialPackingForm = {
  packed_quantity: '', packing_type: 'BOX', packaging_material: '', seal_number: '', batch_number: '',
  handover_to: '', department: '', remarks: '', final_weight: '', hallmark: '', certificate_no: '', photos: []
};
const normalizeRows = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({
  ...row,
  pieces_count: Number(row.pieces_count ?? row.quantity ?? 0),
  target_pieces: Number(row.target_pieces ?? row.pieces_count ?? row.quantity ?? 0),
  manufactured_pieces: Number(row.manufactured_pieces ?? row.pieces_count ?? row.quantity ?? 0),
  total_weight: row.total_weight ?? row.final_weight ?? null,
  stages_summary: Array.isArray(row.stages_summary) ? row.stages_summary : [],
  packing_summary: row.packing_summary || null,
  final_qc_summary: row.final_qc_summary || null,
}));

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

function ConfettiOverlay({ active }) {
  if (!active) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[65] overflow-hidden">
      <style>{`@keyframes qc-fall{0%{transform:translateY(-12vh) rotate(0deg);opacity:1;}100%{transform:translateY(105vh) rotate(720deg);opacity:0;}}`}</style>
      {Array.from({ length: 32 }, (_, idx) => (
        <span
          key={idx}
          className="absolute block h-2.5 w-2.5 rounded-sm"
          style={{
            left: `${(idx * 17) % 100}%`,
            top: '-10vh',
            backgroundColor: ['#22c55e', '#0ea5e9', '#f59e0b', '#f43f5e'][idx % 4],
            animation: `qc-fall ${1.8 + (idx % 7) * 0.12}s linear forwards`,
            animationDelay: `${(idx % 8) * 0.04}s`,
          }}
        />
      ))}
    </div>
  );
}

function QueueCard({ job, title, accent, onAction }) {
  const tone = accent === 'blue' ? 'bg-sky-100 text-sky-700 border-sky-200' : 'bg-amber-100 text-amber-700 border-amber-200';
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl border ${tone}`}><Gem size={18} /></div>
          <div>
            <div className="font-bold leading-tight text-slate-800">{job.design_name || job.design_code || 'Design'}</div>
            <div className="text-xs text-slate-500">{job.job_card_no}</div>
          </div>
        </div>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600">{job.days_in_queue || 0}d</span>
      </div>
      <div className="space-y-2 text-sm text-slate-600">
        <div className="flex items-center justify-between"><span>Client</span><span className="font-medium text-slate-800">{job.client_name || 'N/A'}</span></div>
        <div className="flex items-center justify-between"><span>Target Pieces</span><span className="font-medium text-slate-800">{job.target_pieces}</span></div>
        <div className="flex items-center justify-between"><span>Manufactured Pieces</span><span className="font-medium text-slate-800">{job.manufactured_pieces}</span></div>
        <div className="flex items-center justify-between"><span>Weight</span><span className="font-medium text-slate-800">{job.total_weight ?? 'N/A'} g</span></div>
        <div className="pt-2 text-xs font-medium text-slate-500">{title}</div>
      </div>
      <button onClick={() => onAction(job)} className="mt-5 w-full rounded-lg bg-slate-900 px-4 py-2.5 font-semibold text-white hover:bg-slate-800">
        {accent === 'blue' ? 'Open Packing' : 'Inspect Final QC'}
      </button>
    </div>
  );
}

function PhotoPicker({ photos, onChange }) {
  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const encoded = await Promise.all(files.map(fileToDataUrl));
    onChange([...(photos || []), ...encoded]);
    event.target.value = '';
  };

  return (
    <div className="space-y-3">
      <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center hover:border-sky-400 hover:bg-sky-50">
        <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
        <UploadCloud className="mb-2 text-slate-400" size={22} />
        <span className="text-sm font-semibold text-slate-700">Upload Packing Photos</span>
      </label>
      {photos?.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {photos.map((photo, index) => (
            <div key={`${photo}-${index}`} className="relative overflow-hidden rounded-xl border border-slate-200 bg-white">
              <img src={photo} alt={`Packing ${index + 1}`} className="h-28 w-full object-cover" />
              <button type="button" onClick={() => onChange(photos.filter((_, idx) => idx !== index))} className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-1 text-[11px] font-semibold text-white">Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FinalQCPacking() {
  const navigate = useNavigate();
  const { getOptions } = useMasterData(['PACKING_TYPE', 'HANDOVER_TO']);
  const packingTypes = getOptions('PACKING_TYPE');
  const handoverOptions = getOptions('HANDOVER_TO');
  const location = useLocation();
  const [finalQcQueue, setFinalQcQueue] = useState([]);
  const [packingQueue, setPackingQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedQcJob, setSelectedQcJob] = useState(null);
  const [selectedPackingJob, setSelectedPackingJob] = useState(null);
  const [qcForm, setQcForm] = useState(initialQcForm);
  const [packingForm, setPackingForm] = useState(initialPackingForm);
  const [submittingQc, setSubmittingQc] = useState(false);
  const [savingPacking, setSavingPacking] = useState(false);
  const [movingInventory, setMovingInventory] = useState(false);
  const [successSummary, setSuccessSummary] = useState(null);
  const [confetti, setConfetti] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await productionApi.get('/final-qc/queue');
      const data = response.data?.data || {};
      setFinalQcQueue(normalizeRows(data.final_qc_queue));
      setPackingQueue(normalizeRows(data.packing_queue));
    } catch (err) {
      console.error(err);
      setError('Failed to load Final QC and Packing queues.');
      setFinalQcQueue([]);
      setPackingQueue([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  useEffect(() => {
    const refreshOnFocus = () => loadQueue();
    const intervalId = window.setInterval(refreshOnFocus, 15000);
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [loadQueue]);

  useEffect(() => {
    const handoff = location.state;
    if (!handoff?.handoffStatus) return;

    setConfetti(true);
    const timeoutId = window.setTimeout(() => setConfetti(false), 2400);
    setSuccessSummary({
      mode: handoff.handoffStatus === 'PACKING' ? 'qc' : 'final-qc-ready',
      job_card_no: handoff.job_card_no,
      design_name: handoff.design_name,
      client_name: handoff.client_name,
      pieces_count: handoff.pieces_count,
      final_weight: handoff.final_weight,
    });
    loadQueue();

    return () => window.clearTimeout(timeoutId);
  }, [location.state, loadQueue]);

  const finalQcCount = useMemo(() => finalQcQueue.length, [finalQcQueue]);
  const packingCount = useMemo(() => packingQueue.length, [packingQueue]);

  const openInspectModal = (job) => {
    setSelectedQcJob(job);
    setQcForm({ ...initialQcForm, weight_verified: job.total_weight ?? '', stone_count: job.final_qc_summary?.stone_count ?? '' });
  };

  const openPackingModal = (job) => {
    const packing = job.packing_summary || {};
    setSelectedPackingJob(job);
    setPackingForm({
      packed_quantity: packing.packed_quantity ?? job.pieces_count ?? '',
      packing_type: packing.packing_type || 'BOX',
      packaging_material: packing.packaging_material || '',
      seal_number: packing.seal_number || '',
      batch_number: packing.batch_number || '',
      handover_to: packing.handover_to || '',
      department: packing.department || '',
      remarks: packing.remarks || '',
      final_weight: job.total_weight ?? '',
      hallmark: job.final_qc_summary?.hallmark_ok ? 'HALLMARKED' : '',
      certificate_no: '',
      photos: Array.isArray(packing.photos) ? packing.photos : [],
    });
  };

  const submitReview = async (decision) => {
    if (!selectedQcJob || submittingQc) return;
    setSubmittingQc(true);
    try {
      const payload = {
        decision,
        weight_verified: qcForm.weight_verified === '' ? null : Number(qcForm.weight_verified),
        stone_count: qcForm.stone_count === '' ? null : Number(qcForm.stone_count),
        hallmark_ok: !!qcForm.hallmark_ok,
        finish_grade: qcForm.finish_grade,
        notes: qcForm.notes,
      };
      const response = await productionApi.post(`/final-qc/${selectedQcJob.job_card_id}/review`, payload);
      if (decision === 'APPROVED') {
        try { await productionApi.post(`/final-qc/${selectedQcJob.job_card_id}/generate-certificate`); } catch (certErr) { console.error(certErr); }
        setConfetti(true);
        setTimeout(() => setConfetti(false), 2400);
        setSuccessSummary({
          mode: 'qc',
          job_card_no: selectedQcJob.job_card_no,
          design_name: selectedQcJob.design_name,
          client_name: selectedQcJob.client_name,
          pieces_count: selectedQcJob.pieces_count,
          final_weight: response.data?.data?.final_weight,
        });
      }
      setSelectedQcJob(null);
      await loadQueue();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to submit Final QC review.');
    } finally {
      setSubmittingQc(false);
    }
  };

  const savePackingDetails = async () => {
    if (!selectedPackingJob || savingPacking) return;
    setSavingPacking(true);
    try {
      await productionApi.post(`/final-qc/${selectedPackingJob.job_card_id}/pack`, {
        packed_quantity: packingForm.packed_quantity === '' ? null : Number(packingForm.packed_quantity),
        packing_type: packingForm.packing_type,
        packaging_material: packingForm.packaging_material,
        seal_number: packingForm.seal_number,
        batch_number: packingForm.batch_number,
        handover_to: packingForm.handover_to,
        department: packingForm.department,
        remarks: packingForm.remarks,
        photos: packingForm.photos,
      });
      await loadQueue();
      alert('Packing details saved.');
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to save packing details.');
    } finally {
      setSavingPacking(false);
    }
  };

  const moveToInventory = async () => {
    if (!selectedPackingJob || movingInventory) return;
    setMovingInventory(true);
    try {
      await productionApi.post(`/final-qc/${selectedPackingJob.job_card_id}/pack`, {
        packed_quantity: packingForm.packed_quantity === '' ? null : Number(packingForm.packed_quantity),
        packing_type: packingForm.packing_type,
        packaging_material: packingForm.packaging_material,
        seal_number: packingForm.seal_number,
        batch_number: packingForm.batch_number,
        handover_to: packingForm.handover_to,
        department: packingForm.department,
        remarks: packingForm.remarks,
        photos: packingForm.photos,
      });
      const response = await productionApi.post(`/final-qc/${selectedPackingJob.job_card_id}/move-to-inventory`, {
        final_weight: packingForm.final_weight === '' ? null : Number(packingForm.final_weight),
        hallmark: packingForm.hallmark,
        certificate_no: packingForm.certificate_no,
      });
      setSelectedPackingJob(null);
      setSuccessSummary({
        mode: 'inventory',
        job_card_no: selectedPackingJob.job_card_no,
        design_name: selectedPackingJob.design_name,
        client_name: selectedPackingJob.client_name,
        pieces_count: selectedPackingJob.pieces_count,
        final_weight: response.data?.data?.final_weight,
      });
      await loadQueue();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to move item to inventory.');
    } finally {
      setMovingInventory(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-amber-50/40 p-4 md:p-8">
      <ConfettiOverlay active={confetti} />
      <style>{`@media (max-width: ${BREAKPOINTS.mobile}) {.qc-inspect-modal,.packing-modal{width:100vw!important;height:100vh!important;border-radius:0!important;}}`}</style>
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <button
                type="button"
                onClick={() => navigate('/production')}
                className="mb-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
              >
                <ArrowLeft size={16} />
                Back To Production
              </button>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><ShieldCheck className="text-emerald-600" size={24} /> Final QC and Packing</h1>
              <p className="mt-1 text-slate-600">Approve finished job cards, then record packing details before moving them into store inventory.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 font-semibold text-emerald-800"><ClipboardCheck size={16} /> Final QC: {finalQcCount}</div>
              <div className="inline-flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2 font-semibold text-sky-800"><Archive size={16} /> Packing: {packingCount}</div>
            </div>
          </div>
        </div>
        {error ? <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
        {loading ? <SkeletonLoader count={6} height={128} /> : (
          <div className="space-y-8">
            <section>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-slate-900">Final QC Queue</h2>
                <p className="text-sm text-slate-500">Jobs waiting for final approval.</p>
              </div>
              {finalQcQueue.length === 0 ? (
                <div className="rounded-2xl border border-emerald-100 bg-white p-10 text-center text-slate-500">
                  <CheckCircle2 className="mx-auto mb-3 text-emerald-500" size={40} />
                  No job cards are waiting for Final QC.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {finalQcQueue.map((job) => (
                    <QueueCard key={job.job_card_id} job={job} accent="amber" title={`Reached queue: ${formatDate(job.final_qc_reached_at)}`} onAction={openInspectModal} />
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-slate-900">Packing Queue</h2>
                <p className="text-sm text-slate-500">Final-QC-approved jobs waiting to be packed and moved to inventory.</p>
              </div>
              {packingQueue.length === 0 ? (
                <div className="rounded-2xl border border-sky-100 bg-white p-10 text-center text-slate-500">
                  <PackageCheck className="mx-auto mb-3 text-sky-500" size={40} />
                  No job cards are currently in packing.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {packingQueue.map((job) => (
                    <QueueCard
                      key={job.job_card_id}
                      job={job}
                      accent="blue"
                      title={job.packing_summary?.packed_at ? `Last packed: ${formatDate(job.packing_summary.packed_at)}` : 'Packing details pending'}
                      onAction={openPackingModal}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {selectedQcJob ? (
        <div className="fixed inset-0 z-50 bg-slate-900/70 p-3 backdrop-blur-sm md:p-6">
          <div className="qc-inspect-modal flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Final QC Inspection</h2>
                <p className="text-sm text-slate-500">{selectedQcJob.job_card_no} • {selectedQcJob.design_name}</p>
              </div>
              <button onClick={() => !submittingQc && setSelectedQcJob(null)} className="font-semibold text-slate-500 hover:text-slate-700">Close</button>
            </div>

            <div className="grid flex-1 grid-cols-1 gap-6 overflow-y-auto p-6 xl:grid-cols-2">
              <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-5">
                <h3 className="mb-3 font-semibold text-slate-800">Job Card Summary</h3>
                <div className="space-y-2 text-sm text-slate-700">
                  <div className="flex justify-between"><span>Design</span><span className="font-medium">{selectedQcJob.design_name || selectedQcJob.design_code}</span></div>
                  <div className="flex justify-between"><span>Client</span><span className="font-medium">{selectedQcJob.client_name || 'N/A'}</span></div>
                  <div className="flex justify-between"><span>Target Pieces</span><span className="font-medium">{selectedQcJob.target_pieces}</span></div>
                  <div className="flex justify-between"><span>Manufactured Pieces</span><span className="font-medium">{selectedQcJob.manufactured_pieces}</span></div>
                  <div className="flex justify-between"><span>Reached Queue</span><span className="font-medium">{formatDate(selectedQcJob.final_qc_reached_at)}</span></div>
                </div>
                <div className="mt-5">
                  <h4 className="mb-2 text-sm font-semibold text-slate-800">Stages Timeline</h4>
                  <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                    {selectedQcJob.stages_summary.map((stage) => (
                      <div key={stage.stage_id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                        <div className="font-medium text-slate-800">{stage.stage_name}</div>
                        <div className="mt-1 text-xs text-slate-500">{stage.status} • {formatDate(stage.completed_at)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-slate-200 p-5">
                <h3 className="mb-3 font-semibold text-slate-800">Inspection Form</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Weight Verified (g)</div><input type="number" step="0.001" value={qcForm.weight_verified} onChange={(e) => setQcForm((prev) => ({ ...prev, weight_verified: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Stone Count</div><input type="number" value={qcForm.stone_count} onChange={(e) => setQcForm((prev) => ({ ...prev, stone_count: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
                  <label className="text-sm md:col-span-2 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2"><input type="checkbox" checked={qcForm.hallmark_ok} onChange={(e) => setQcForm((prev) => ({ ...prev, hallmark_ok: e.target.checked }))} /><span className="font-medium text-slate-700">Hallmark Check Passed</span></label>
                  <label className="text-sm md:col-span-2"><div className="mb-1 font-medium text-slate-700">Finish Quality Grade</div><select value={qcForm.finish_grade} onChange={(e) => setQcForm((prev) => ({ ...prev, finish_grade: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2"><option value="A">A</option><option value="B">B</option><option value="C">C</option></select></label>
                  <label className="text-sm md:col-span-2"><div className="mb-1 font-medium text-slate-700">Notes</div><textarea rows={4} value={qcForm.notes} onChange={(e) => setQcForm((prev) => ({ ...prev, notes: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Inspection observations" /></label>
                </div>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <ApiButton loading={submittingQc} onClick={() => submitReview('APPROVED')} variant="success" className="flex-1"><Sparkles size={16} /> APPROVE TO PACKING</ApiButton>
                  <ApiButton loading={submittingQc} onClick={() => submitReview('REJECTED')} variant="danger" className="flex-1">REJECT</ApiButton>
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {selectedPackingJob ? (
        <div className="fixed inset-0 z-50 bg-slate-900/70 p-3 backdrop-blur-sm md:p-6">
          <div className="packing-modal flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Packing and Inventory Handover</h2>
                <p className="text-sm text-slate-500">{selectedPackingJob.job_card_no} • {selectedPackingJob.design_name}</p>
              </div>
              <button onClick={() => !savingPacking && !movingInventory && setSelectedPackingJob(null)} className="font-semibold text-slate-500 hover:text-slate-700">Close</button>
            </div>

            <div className="grid flex-1 grid-cols-1 gap-6 overflow-y-auto p-6 xl:grid-cols-[0.95fr,1.05fr]">
              <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-5">
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><Box size={16} className="text-sky-600" /> Job and QC Summary</h3>
                <div className="space-y-2 text-sm text-slate-700">
                  <div className="flex justify-between"><span>Client</span><span className="font-medium">{selectedPackingJob.client_name || 'N/A'}</span></div>
                  <div className="flex justify-between"><span>Target Pieces</span><span className="font-medium">{selectedPackingJob.target_pieces}</span></div>
                  <div className="flex justify-between"><span>Manufactured Pieces</span><span className="font-medium">{selectedPackingJob.manufactured_pieces}</span></div>
                  <div className="flex justify-between"><span>Final Weight</span><span className="font-medium">{selectedPackingJob.total_weight ?? 'N/A'} g</span></div>
                  <div className="flex justify-between"><span>QC Approved</span><span className="font-medium">{formatDate(selectedPackingJob.final_qc_summary?.inspected_at)}</span></div>
                  <div className="flex justify-between"><span>Hallmark Check</span><span className="font-medium">{selectedPackingJob.final_qc_summary?.hallmark_ok ? 'Passed' : 'Pending/No'}</span></div>
                </div>
                <div className="mt-5"><h4 className="mb-2 text-sm font-semibold text-slate-800">Packing Photos</h4><PhotoPicker photos={packingForm.photos} onChange={(photos) => setPackingForm((prev) => ({ ...prev, photos }))} /></div>
              </section>

              <section className="rounded-xl border border-slate-200 p-5">
                <h3 className="mb-3 font-semibold text-slate-800">Packing Form</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Pieces Produced</div><input type="number" value={packingForm.packed_quantity} onChange={(e) => setPackingForm((prev) => ({ ...prev, packed_quantity: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Packing Type</div><select value={packingForm.packing_type} onChange={(e) => setPackingForm((prev) => ({ ...prev, packing_type: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2">{packingTypes.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Packaging Details</div><input value={packingForm.packaging_material} onChange={(e) => setPackingForm((prev) => ({ ...prev, packaging_material: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Velvet Box / Zip Pouch" /></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Handover To</div><select value={packingForm.handover_to} onChange={(e) => setPackingForm((prev) => ({ ...prev, handover_to: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2"><option value="">Select handover</option>{handoverOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Seal Number</div><input value={packingForm.seal_number} onChange={(e) => setPackingForm((prev) => ({ ...prev, seal_number: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Batch Number</div><input value={packingForm.batch_number} onChange={(e) => setPackingForm((prev) => ({ ...prev, batch_number: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Department</div><input value={packingForm.department} onChange={(e) => setPackingForm((prev) => ({ ...prev, department: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Retail / Dispatch" /></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Final Weight for Inventory (g)</div><input type="number" step="0.001" value={packingForm.final_weight} onChange={(e) => setPackingForm((prev) => ({ ...prev, final_weight: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Hallmark</div><input value={packingForm.hallmark} onChange={(e) => setPackingForm((prev) => ({ ...prev, hallmark: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="HALLMARKED" /></label>
                  <label className="text-sm"><div className="mb-1 font-medium text-slate-700">Certificate No</div><input value={packingForm.certificate_no} onChange={(e) => setPackingForm((prev) => ({ ...prev, certificate_no: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="QC certificate reference" /></label>
                  <label className="text-sm md:col-span-2"><div className="mb-1 font-medium text-slate-700">Remarks</div><textarea rows={4} value={packingForm.remarks} onChange={(e) => setPackingForm((prev) => ({ ...prev, remarks: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Packaging notes, seal checks, handover comments" /></label>
                </div>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <ApiButton loading={savingPacking} onClick={savePackingDetails} variant="primary" className="flex-1">Save Packing Details</ApiButton>
                  <ApiButton loading={movingInventory} onClick={moveToInventory} variant="success" className="flex-1"><PackageCheck size={16} /> Move to Inventory</ApiButton>
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {successSummary ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/65 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-emerald-200 bg-white p-6 shadow-2xl">
            <div className="mb-3 flex items-center gap-2 text-lg font-bold text-emerald-700"><CheckCircle2 size={20} /> {successSummary.mode === 'inventory' ? 'Moved to Inventory' : successSummary.mode === 'final-qc-ready' ? 'Sent to Final QC Queue' : 'Final QC Approved'}</div>
            <div className="space-y-2 rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-slate-700">
              <div className="flex justify-between"><span>Job Card</span><span className="font-semibold">{successSummary.job_card_no}</span></div>
              <div className="flex justify-between"><span>Design</span><span className="font-semibold">{successSummary.design_name || 'N/A'}</span></div>
              <div className="flex justify-between"><span>Client</span><span className="font-semibold">{successSummary.client_name || 'N/A'}</span></div>
              <div className="flex justify-between"><span>Pieces</span><span className="font-semibold">{successSummary.pieces_count}</span></div>
              <div className="flex justify-between"><span>Final Weight</span><span className="font-semibold">{successSummary.final_weight ?? 'N/A'} g</span></div>
              <div className="pt-2 text-xs font-medium text-emerald-800">{successSummary.mode === 'inventory' ? 'This job card is now in store inventory and marked Ready for Dispatch.' : successSummary.mode === 'final-qc-ready' ? 'This job card has completed production and is now waiting in the Final QC queue.' : 'This job card has moved into the Packing queue.'}</div>
            </div>
            <div className="mt-5 flex gap-3"><button onClick={() => setSuccessSummary(null)} className="flex-1 rounded-lg bg-slate-800 px-4 py-2.5 font-semibold text-white hover:bg-slate-900">Close</button></div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
